import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ScheduleDayComponent } from 'app/components/schedule/ScheduleDayComponent'
import { SlotCandidatesPanel } from 'app/components/schedule/SlotCandidatesPanel'
import { PlacedOpening } from 'app/components/schedule/scheduleDay'
import { PlacedSlot } from 'app/components/schedule/scheduleGrid'
import { formatMinutes } from 'lib/dayClock'
import { useScheduleDay } from 'app/components/schedule/useScheduleDay'
import { useSchedulePage } from 'app/components/schedule/useSchedulePage'
import { useHandleSearchParams } from 'app/utils'

/**
 * The clicked opening, in the candidates panel's slot shape. `types` stays
 * empty on purpose: an opening carries no categories, and an empty list reads
 * as unknown to the ranking, which offers everybody instead of filtering.
 */
function asSlot(date: string, placed: PlacedOpening, room: string): PlacedSlot {
  return {
    slot: {
      date,
      time: formatMinutes(placed.start),
      start: placed.start,
      count: 1,
      types: [],
      room,
    },
    start: placed.start,
    end: placed.end,
  }
}

function SchedulePage() {
  const page = useSchedulePage()
  const [searchParams] = useSearchParams()
  const [chosen, setChosen] = useState<{ date: string; slot: PlacedSlot } | undefined>()

  // In the URL so a day can be shared and survives a reload.
  const date = searchParams.get(`date`) || moment().format(`YYYY-MM-DD`)
  const day = useScheduleDay(page.locationId, date)
  const handleSearchParams = useHandleSearchParams()
  const goToDate = useCallback(
    // `wId: undefined` keeps WorkspaceGate's own stamp out of the write; a
    // stale copy costs a workspace switch and a full reload.
    (next: string) => {
      handleSearchParams({ date: next, wId: undefined })
    },
    [handleSearchParams],
  )

  const board = useMemo(() => ({ ...day, goToDate }), [day, goToDate])

  return (
    <>
      <ScheduleDayComponent
        day={board}
        locationName={page.locationName}
        locations={page.locations}
        excludedLocations={page.excludedLocations}
        pmsGate={page.pmsGate}
        locationId={page.locationId}
        loadingLocations={page.loadingLocations}
        asking={page.asking}
        onLocation={page.onLocation}
        onSelectOpening={(when, placed, room) => setChosen({ date: when, slot: asSlot(when, placed, room) })}
      />
      <SlotCandidatesPanel
        open={Boolean(chosen)}
        date={chosen?.date}
        slot={chosen?.slot}
        locationId={page.locationId}
        onClose={() => setChosen(undefined)}
      />
    </>
  )
}

export default SchedulePage
